import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, Circle } from "lucide-react";
import {
    WorkspaceTabs,
    DecisionTree,
    StepTimeline,
    WorkspaceHero,
} from "../components/workspace/Primitives";

const TABS = [
    { key: "classifier", label: "Entity classifier" },
    { key: "measures", label: "Article 21 measures" },
    { key: "incident", label: "Incident reporting" },
    { key: "liability", label: "Management liability" },
    { key: "transposition", label: "Transposition by country" },
];

const STATUS_BADGE = {
    transposed: { label: "Transposed", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-900 border-amber-200" },
    overdue: { label: "Overdue", cls: "bg-rose-50 text-rose-900 border-rose-200" },
};

export default function Nis2Workspace() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("classifier");

    useEffect(() => {
        api.get("/nis2").then(({ data }) => setData(data));
    }, []);

    if (!data) {
        return (
            <div className="container-edit py-14 flex items-center gap-3 text-[#0D1B3E]/60">
                <Loader2 className="animate-spin" size={18} />
                <span className="label-eyebrow">Loading NIS2 workspace…</span>
            </div>
        );
    }

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="nis2-workspace">
            <WorkspaceHero
                eyebrow="Workspace · Directive (EU) 2022/2555"
                title="NIS2"
                subtitle="Classify your entity, implement Article 21 measures, walk through the incident-reporting timeline, understand management liability, and check transposition by country."
                badges={["Transposition deadline 17 October 2024", "Most MS now transposed"]}
            />

            <div className="mt-8">
                <WorkspaceTabs tabs={TABS} active={tab} onChange={setTab} testIdPrefix="nis2" />
            </div>

            <div className="mt-9">
                {tab === "classifier" && (
                    <section className="grid lg:grid-cols-[1.5fr_1fr] gap-7" data-testid="nis2-classifier">
                        <DecisionTree
                            tree={data.entity_classifier}
                            testIdPrefix="nis2-classifier"
                            renderOutcome={(key, outcome) => (
                                <div
                                    className="card-flat border-2 border-[#C9A84C] p-7"
                                    data-testid={`nis2-classifier-outcome-${key}`}
                                >
                                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                        Classification
                                    </div>
                                    <h3 className="mt-2 font-display text-2xl tracking-tight">
                                        {outcome.label}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#0D1B3E]/85 leading-relaxed">
                                        {outcome.body}
                                    </p>
                                    {outcome.obligations_summary?.length > 0 && (
                                        <div className="mt-5">
                                            <div className="label-eyebrow">Obligations</div>
                                            <ul className="mt-2 space-y-1.5 text-sm text-[#0D1B3E]/85">
                                                {outcome.obligations_summary.map((o, i) => (
                                                    <li key={i} className="flex gap-2">
                                                        <span className="text-[#C9A84C]">–</span>
                                                        <span>{o}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {outcome.max_fine && outcome.max_fine !== "—" && (
                                        <div className="mt-5 inline-block px-3 py-1.5 bg-[#0D1B3E] text-[#F5F0E8] text-xs uppercase tracking-[0.18em]">
                                            Max fine · {outcome.max_fine}
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                        <aside className="space-y-5">
                            <div className="card-flat p-5">
                                <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                    Annex I: essential candidate sectors
                                </div>
                                <ul className="mt-3 space-y-1 text-sm text-[#0D1B3E]/85">
                                    {data.entity_classifier.annex_i_sectors.map((s, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span>–</span>
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="card-flat p-5">
                                <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                    Annex II: important candidate sectors
                                </div>
                                <ul className="mt-3 space-y-1 text-sm text-[#0D1B3E]/85">
                                    {data.entity_classifier.annex_ii_sectors.map((s, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span>–</span>
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </aside>
                    </section>
                )}

                {tab === "measures" && <MeasuresChecklist measures={data.article_21_measures} />}

                {tab === "incident" && (
                    <section className="max-w-2xl" data-testid="nis2-incident">
                        <StepTimeline items={data.incident_reporting} testIdPrefix="nis2-incident" />
                    </section>
                )}

                {tab === "liability" && (
                    <section className="max-w-3xl" data-testid="nis2-liability">
                        <p className="text-[#0D1B3E]/85 leading-relaxed">
                            {data.management_liability.summary}
                        </p>
                        <ul className="mt-7 space-y-3">
                            {data.management_liability.duties.map((d, i) => (
                                <li
                                    key={i}
                                    className="card-flat p-5"
                                    data-testid={`nis2-liability-${i}`}
                                >
                                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                        {d.article}
                                    </div>
                                    <div className="mt-1 font-display text-lg tracking-tight">
                                        {d.title}
                                    </div>
                                    <p className="mt-2 text-sm text-[#0D1B3E]/85 leading-relaxed">
                                        {d.body}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {tab === "transposition" && (
                    <TranspositionTable rows={data.transposition} />
                )}
            </div>
        </div>
    );
}

function MeasuresChecklist({ measures }) {
    const [checked, setChecked] = useState(() => {
        if (typeof window === "undefined") return {};
        try {
            return JSON.parse(localStorage.getItem("nis2_art21_v1") || "{}");
        } catch {
            return {};
        }
    });
    const total = measures.length;
    const done = measures.filter((m) => checked[m.id]).length;
    const pct = Math.round((done / total) * 100);
    const toggle = (id) => {
        setChecked((c) => {
            const next = { ...c, [id]: !c[id] };
            try {
                localStorage.setItem("nis2_art21_v1", JSON.stringify(next));
            } catch {
                /* noop */
            }
            return next;
        });
    };
    return (
        <section data-testid="nis2-measures">
            <div className="card-flat p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Article 21 coverage</div>
                    <div className="mt-1 font-display text-3xl tracking-tight">
                        {pct}%{" "}
                        <span className="text-sm text-[#0D1B3E]/60 font-normal">
                            ({done} / {total})
                        </span>
                    </div>
                </div>
                <div className="w-full max-w-xs">
                    <div className="h-2 bg-[#0D1B3E]/10">
                        <div
                            className="h-full bg-[#C9A84C] transition-all"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
            </div>
            <ul className="mt-6 space-y-2.5">
                {measures.map((m) => {
                    const ok = !!checked[m.id];
                    return (
                        <li key={m.id} className="card-flat p-4 flex items-start gap-3">
                            <button
                                type="button"
                                onClick={() => toggle(m.id)}
                                data-testid={`nis2-measure-${m.id}`}
                                className="shrink-0 mt-0.5"
                                aria-pressed={ok}
                            >
                                {ok ? (
                                    <CheckCircle2 size={20} color="#C9A84C" />
                                ) : (
                                    <Circle size={20} className="text-[#0D1B3E]/30" />
                                )}
                            </button>
                            <span
                                className={`text-sm leading-relaxed ${
                                    ok
                                        ? "text-[#0D1B3E]/55 line-through"
                                        : "text-[#0D1B3E]/90"
                                }`}
                            >
                                {m.label}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function TranspositionTable({ rows }) {
    const [filter, setFilter] = useState("all");
    const counts = useMemo(() => {
        const c = { all: rows.length, transposed: 0, in_progress: 0, overdue: 0 };
        rows.forEach((r) => {
            c[r.status] = (c[r.status] || 0) + 1;
        });
        return c;
    }, [rows]);
    const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);
    return (
        <section data-testid="nis2-transposition">
            <div className="flex items-center gap-2 flex-wrap">
                {[
                    { key: "all", label: `All (${counts.all})` },
                    { key: "transposed", label: `Transposed (${counts.transposed})` },
                    { key: "in_progress", label: `In progress (${counts.in_progress})` },
                ].map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                            filter === f.key
                                ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-[#0D1B3E]"
                        }`}
                        data-testid={`nis2-trans-filter-${f.key}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            <ul className="mt-6 grid gap-3 md:grid-cols-2">
                {filtered.map((r) => {
                    const badge = STATUS_BADGE[r.status] || STATUS_BADGE.in_progress;
                    return (
                        <li
                            key={r.code}
                            className="card-flat p-5"
                            data-testid={`nis2-trans-${r.code}`}
                        >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="font-display text-xl tracking-tight">
                                    {r.country}{" "}
                                    <span className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/50">
                                        {r.code}
                                    </span>
                                </div>
                                <span
                                    className={`text-[10px] font-semibold uppercase tracking-[0.16em] px-2 py-0.5 border ${badge.cls}`}
                                >
                                    {badge.label}
                                </span>
                            </div>
                            <div className="mt-3 text-sm text-[#0D1B3E]/85">{r.act}</div>
                            <p className="mt-2 text-xs text-[#0D1B3E]/65 leading-relaxed">
                                {r.notes}
                            </p>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
